import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarIcon, Users, Check, Upload, Plus, Trash2, User, Mail, Key, Camera, ScanLine, Sun, X, Info, ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { parseMRZ } from '../lib/mrzParser';

import { API } from '../lib/api';

// Translations
const translations = {
  it: {
    documentTypes: [
      { value: 'carta_identita', label: "Carta d'Identità" },
      { value: 'passaporto', label: 'Passaporto' },
      { value: 'patente', label: 'Patente di Guida' },
    ],
    nationalities: ['Italiana', 'Tedesca', 'Francese', 'Inglese', 'Spagnola', 'Americana', 'Olandese', 'Belga', 'Svizzera', 'Austriaca', 'Altra'],
    onlineCheckin: 'Check-in Online',
    step1: 'Verifica Prenotazione',
    step2: 'Dati Ospiti',
    step3: 'Conferma',
    bookingVerification: 'Verifica Prenotazione',
    enterBookingDetails: 'Inserisci i dati della tua prenotazione per procedere con il check-in',
    bookingEmail: 'Email Prenotazione',
    bookingCode: 'Codice Prenotazione',
    verifyAndContinue: 'Verifica e Continua',
    verifying: 'Verifica in corso...',
    stayDetails: 'Dettagli Soggiorno',
    arrivalDate: 'Data Arrivo',
    departureDate: 'Data Partenza',
    numberOfGuests: 'Numero Ospiti',
    selectDate: 'Seleziona data',
    guest: 'ospite',
    guests: 'ospiti',
    mainGuest: 'Ospite Principale',
    mainGuestData: 'Dati Ospite Principale',
    requiredForPolice: 'Obbligatorio per la Questura',
    firstName: 'Nome',
    lastName: 'Cognome',
    birthDate: 'Data di Nascita',
    birthPlace: 'Luogo di Nascita',
    nationality: 'Nazionalità',
    documentType: 'Tipo Documento',
    documentNumber: 'Numero Documento',
    documentExpiry: 'Scadenza Documento',
    documentFront: 'Fronte Documento',
    documentBack: 'Retro Documento',
    uploadPhoto: 'Carica foto',
    uploading: 'Caricamento...',
    companions: 'Accompagnatori',
    additionalGuests: 'Inserisci i dati degli altri ospiti',
    addCompanion: 'Aggiungi Accompagnatore',
    companion: 'Accompagnatore',
    remove: 'Rimuovi',
    notes: 'Note Aggiuntive',
    specialRequests: 'Richieste speciali, orario arrivo previsto, ecc.',
    back: 'Indietro',
    continue: 'Continua',
    confirm: 'Conferma Check-in',
    submitting: 'Invio in corso...',
    summary: 'Riepilogo Check-in',
    reviewData: 'Verifica i dati prima di confermare',
    period: 'Periodo',
    to: 'al',
    totalGuests: 'Ospiti totali',
    checkinSuccess: 'Check-in completato con successo!',
    checkinSuccessDesc: 'I tuoi dati sono stati registrati. Riceverai una conferma via email.',
    backToDashboard: 'Torna alla Dashboard',
    bookingNotFound: 'Prenotazione non trovata. Verifica i dati inseriti.',
    errorVerifying: 'Errore durante la verifica',
    errorSubmitting: 'Errore durante l\'invio',
    documentUploaded: 'Documento caricato!',
    errorUploading: 'Errore durante il caricamento',
    fillAllFields: 'Compila tutti i campi obbligatori dell\'ospite principale',
    invalidDates: 'Seleziona date valide',
    documentRequired: 'Carica almeno il fronte del documento dell\'ospite principale',
    selectDocType: 'Seleziona tipo documento',
    selectNationality: 'Seleziona nazionalità'
  },
  en: {
    documentTypes: [
      { value: 'carta_identita', label: "ID Card" },
      { value: 'passaporto', label: 'Passport' },
      { value: 'patente', label: "Driver's License" },
    ],
    nationalities: ['Italian', 'German', 'French', 'British', 'Spanish', 'American', 'Dutch', 'Belgian', 'Swiss', 'Austrian', 'Other'],
    onlineCheckin: 'Online Check-in',
    step1: 'Verify Booking',
    step2: 'Guest Data',
    step3: 'Confirm',
    bookingVerification: 'Booking Verification',
    enterBookingDetails: 'Enter your booking details to proceed with check-in',
    bookingEmail: 'Booking Email',
    bookingCode: 'Booking Code',
    verifyAndContinue: 'Verify and Continue',
    verifying: 'Verifying...',
    stayDetails: 'Stay Details',
    arrivalDate: 'Arrival Date',
    departureDate: 'Departure Date',
    numberOfGuests: 'Number of Guests',
    selectDate: 'Select date',
    guest: 'guest',
    guests: 'guests',
    mainGuest: 'Main Guest',
    mainGuestData: 'Main Guest Data',
    requiredForPolice: 'Required for Police Registration',
    firstName: 'First Name',
    lastName: 'Last Name',
    birthDate: 'Date of Birth',
    birthPlace: 'Place of Birth',
    nationality: 'Nationality',
    documentType: 'Document Type',
    documentNumber: 'Document Number',
    documentExpiry: 'Document Expiry',
    documentFront: 'Document Front',
    documentBack: 'Document Back',
    uploadPhoto: 'Upload photo',
    uploading: 'Uploading...',
    companions: 'Companions',
    additionalGuests: 'Enter data for additional guests',
    addCompanion: 'Add Companion',
    companion: 'Companion',
    remove: 'Remove',
    notes: 'Additional Notes',
    specialRequests: 'Special requests, expected arrival time, etc.',
    back: 'Back',
    continue: 'Continue',
    confirm: 'Confirm Check-in',
    submitting: 'Submitting...',
    summary: 'Check-in Summary',
    reviewData: 'Review data before confirming',
    period: 'Period',
    to: 'to',
    totalGuests: 'Total guests',
    checkinSuccess: 'Check-in completed successfully!',
    checkinSuccessDesc: 'Your data has been registered. You will receive a confirmation email.',
    backToDashboard: 'Back to Dashboard',
    bookingNotFound: 'Booking not found. Please verify the entered data.',
    errorVerifying: 'Error during verification',
    errorSubmitting: 'Error during submission',
    documentUploaded: 'Document uploaded!',
    errorUploading: 'Error during upload',
    fillAllFields: 'Fill all required fields for the main guest',
    invalidDates: 'Select valid dates',
    documentRequired: 'Upload at least the front of the main guest document',
    selectDocType: 'Select document type',
    selectNationality: 'Select nationality'
  }
};

export default function CheckInPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  const dateLocale = language === 'en' ? enUS : it;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [arrivo, setArrivo] = useState(null);
  const [partenza, setPartenza] = useState(null);
  const [numOspiti, setNumOspiti] = useState('1');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Campi per validazione prenotazione
  const [emailPrenotazione, setEmailPrenotazione] = useState('');
  const [codicePrenotazione, setCodicePrenotazione] = useState('');
  
  // Ospite principale
  const [ospitePrincipale, setOspitePrincipale] = useState({
    nome: '',
    cognome: '',
    data_nascita: '',
    luogo_nascita: '',
    nazionalita: 'Italiana',
    tipo_documento: 'carta_identita',
    numero_documento: '',
    scadenza_documento: '',
    foto_fronte_url: '',
    foto_retro_url: ''
  });
  
  // Accompagnatori
  const [accompagnatori, setAccompagnatori] = useState([]);
  
  // Upload state
  const [uploadingFronte, setUploadingFronte] = useState(false);
  const [uploadingRetro, setUploadingRetro] = useState(false);
  const [scanningMRZ, setScanningMRZ] = useState(false);
  const [showMRZGuide, setShowMRZGuide] = useState(false);
  
  // Pending booking state
  const [pendingBooking, setPendingBooking] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(true);
  
  // Check if running on native platform
  const isNative = Capacitor.isNativePlatform();

  // Fetch pending booking that needs check-in
  useEffect(() => {
    const fetchPendingBooking = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/bookings/pending-checkin`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.has_pending_booking && response.data.booking) {
          const booking = response.data.booking;
          setPendingBooking(booking);
          
          // Auto-fill form data from booking
          setCodicePrenotazione(booking.codice_prenotazione || '');
          setEmailPrenotazione(booking.email_ospite || '');
          
          if (booking.data_arrivo) {
            setArrivo(new Date(booking.data_arrivo));
          }
          if (booking.data_partenza) {
            setPartenza(new Date(booking.data_partenza));
          }
          if (booking.num_ospiti) {
            setNumOspiti(String(booking.num_ospiti));
          }
          
          // Pre-fill guest name from booking
          if (booking.nome_ospite) {
            const nameParts = booking.nome_ospite.split(' ');
            setOspitePrincipale(prev => ({
              ...prev,
              nome: nameParts[0] || '',
              cognome: nameParts.slice(1).join(' ') || ''
            }));
          }
          
          toast.info(language === 'en' 
            ? 'Booking found! Complete your check-in.' 
            : 'Prenotazione trovata! Completa il check-in.');
        }
      } catch (error) {
        console.error('Error fetching pending booking:', error);
      } finally {
        setLoadingBooking(false);
      }
    };
    
    fetchPendingBooking();
  }, [token, language]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
    // Pre-fill with user data (only if no booking data)
    if (user && !pendingBooking) {
      setOspitePrincipale(prev => ({
        ...prev,
        nome: prev.nome || user.nome || '',
        cognome: prev.cognome || user.cognome || ''
      }));
    }
  }, [user, authLoading, navigate, pendingBooking]);

  const handleUpload = async (file, type) => {
    if (!file) return;
    
    const setUploading = type === 'fronte' ? setUploadingFronte : setUploadingRetro;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 120 second timeout for slow connections
      });
      
      const url = response.data.url;
      setOspitePrincipale(prev => ({
        ...prev,
        [type === 'fronte' ? 'foto_fronte_url' : 'foto_retro_url']: url
      }));
      toast.success(`Foto ${type} caricata!`);
    } catch (error) {
      console.error('Upload error:', error);
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network')) {
        toast.error('Errore di rete. Verifica la connessione e riprova.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Timeout: il caricamento ha impiegato troppo tempo.');
      } else {
        toast.error(error.response?.data?.detail || 'Errore nel caricamento. Riprova.');
      }
    } finally {
      setUploading(false);
    }
  };

  // Native camera capture using Capacitor
  const handleCameraCapture = async (type, source = CameraSource.Prompt) => {
    const setUploading = type === 'fronte' ? setUploadingFronte : setUploadingRetro;
    setUploading(true);
    
    try {
      // Check and request permissions first
      const permissions = await CapacitorCamera.checkPermissions();
      
      if (permissions.camera !== 'granted' || permissions.photos !== 'granted') {
        const requested = await CapacitorCamera.requestPermissions();
        if (requested.camera !== 'granted' && source !== CameraSource.Photos) {
          toast.error('Permesso fotocamera negato. Abilita nelle impostazioni del dispositivo.');
          setUploading(false);
          return;
        }
      }
      
      // Capture photo using Capacitor Camera
      const image = await CapacitorCamera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source, // CameraSource.Prompt shows both options
        width: 1200,
        height: 1600,
        correctOrientation: true,
        promptLabelHeader: language === 'en' ? 'Document Photo' : 'Foto Documento',
        promptLabelPhoto: language === 'en' ? 'From Library' : 'Dalla Libreria',
        promptLabelPicture: language === 'en' ? 'Take Photo' : 'Scatta Foto',
        promptLabelCancel: language === 'en' ? 'Cancel' : 'Annulla'
      });
      
      // Convert base64 to blob for upload
      const base64Response = await fetch(`data:image/${image.format};base64,${image.base64String}`);
      const blob = await base64Response.blob();
      const file = new File([blob], `document_${type}_${Date.now()}.${image.format}`, {
        type: `image/${image.format}`
      });
      
      // Upload the file
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      const url = response.data.url;
      setOspitePrincipale(prev => ({
        ...prev,
        [type === 'fronte' ? 'foto_fronte_url' : 'foto_retro_url']: url
      }));
      toast.success(language === 'en' ? `${type} photo uploaded!` : `Foto ${type} caricata!`);
      
    } catch (error) {
      console.error('Camera/Upload error:', error);
      
      // User cancelled - don't show error
      if (error.message?.includes('User cancelled') || error.message?.includes('cancelled')) {
        setUploading(false);
        return;
      }
      
      // Permission denied
      if (error.message?.includes('permission') || error.message?.includes('denied')) {
        toast.error(language === 'en' 
          ? 'Permission denied. Enable camera access in device settings.' 
          : 'Permesso negato. Abilita l\'accesso alla fotocamera nelle impostazioni.');
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network')) {
        toast.error(language === 'en' ? 'Network error. Check connection and retry.' : 'Errore di rete. Verifica la connessione e riprova.');
      } else {
        toast.error(error.response?.data?.detail || (language === 'en' ? 'Error capturing photo. Please retry.' : 'Errore nella cattura. Riprova.'));
      }
    } finally {
      setUploading(false);
    }
  };

  // MRZ Scanner function - uses backend OCR for all platforms
  const handleMRZScan = async () => {
    setScanningMRZ(true);
    
    try {
      // Check camera permissions
      const permissions = await CapacitorCamera.checkPermissions();
      if (permissions.camera !== 'granted') {
        const requested = await CapacitorCamera.requestPermissions();
        if (requested.camera !== 'granted') {
          toast.error(language === 'en' 
            ? 'Camera permission required for scanning' 
            : 'Permesso fotocamera richiesto per la scansione');
          setScanningMRZ(false);
          return;
        }
      }
      
      // Capture image of document back (where MRZ is located)
      const image = await CapacitorCamera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        width: 1920,
        height: 1080,
        correctOrientation: true,
        promptLabelHeader: language === 'en' ? 'Scan Document MRZ' : 'Scansiona MRZ Documento',
        promptLabelPicture: language === 'en' ? 'Take Photo' : 'Scatta Foto',
        promptLabelCancel: language === 'en' ? 'Cancel' : 'Annulla'
      });
      
      toast.info(language === 'en' ? 'Analyzing document...' : 'Analisi documento in corso...');
      
      // Convert base64 to Blob and send to backend OCR
      const base64Data = image.base64String;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      const file = new File([blob], 'document.jpg', { type: 'image/jpeg' });
      
      // Send to backend for OCR processing
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/document`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        
        // Auto-fill form with parsed data
        setOspitePrincipale(prev => ({
          ...prev,
          nome: data.nome || prev.nome,
          cognome: data.cognome || prev.cognome,
          data_nascita: data.data_nascita || prev.data_nascita,
          nazionalita: data.nazionalita || prev.nazionalita,
          tipo_documento: data.tipo_documento === 'ID_CARD' ? 'carta_identita' : data.tipo_documento === 'PASSPORT' ? 'passaporto' : prev.tipo_documento,
          numero_documento: data.numero_documento || prev.numero_documento,
          scadenza_documento: data.scadenza_documento || prev.scadenza_documento
        }));
        
        toast.success(language === 'en' 
          ? `Data extracted! Found: ${data.nome || ''} ${data.cognome || ''}` 
          : `Dati estratti! Trovato: ${data.nome || ''} ${data.cognome || ''}`);
      } else {
        toast.error(language === 'en' 
          ? 'Could not extract data. Try with better lighting or enter manually.' 
          : 'Impossibile estrarre i dati. Riprova con migliore illuminazione o inserisci manualmente.');
      }
      
    } catch (error) {
      console.error('MRZ Scan error:', error);
      
      if (error.message?.includes('User cancelled') || error.message?.includes('cancelled')) {
        // User cancelled, no error needed
      } else if (error.message?.includes('permission')) {
        toast.error(language === 'en' 
          ? 'Camera permission denied' 
          : 'Permesso fotocamera negato');
      } else if (error.response?.status === 503) {
        toast.error(language === 'en' 
          ? 'OCR service not available. Please enter data manually.' 
          : 'Servizio OCR non disponibile. Inserisci i dati manualmente.');
      } else {
        toast.error(language === 'en' 
          ? 'Scanning error. Please try again.' 
          : 'Errore scansione. Riprova.');
      }
    } finally {
      setScanningMRZ(false);
    }
  };

  // MRZ Scanner for WEB - uses backend OCR for better accuracy
  const handleMRZScanWeb = async (file) => {
    if (!file) return;
    
    setScanningMRZ(true);
    toast.info(language === 'en' ? 'Analyzing document... This may take a few seconds.' : 'Analisi documento in corso... Potrebbe richiedere alcuni secondi.');
    
    try {
      // Send to backend for OCR processing
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/ocr/document`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout for OCR
      });
      
      console.log('Backend OCR Response:', response.data);
      
      if (!response.data.success) {
        toast.error(language === 'en' 
          ? 'Could not process the image. Try with a clearer photo.' 
          : 'Impossibile processare l\'immagine. Prova con una foto più nitida.');
        setScanningMRZ(false);
        return;
      }
      
      const mrzData = response.data.data;
      
      if (!mrzData) {
        // Show raw text for debugging
        console.log('Raw OCR text (no MRZ found):', response.data.raw_text);
        toast.error(language === 'en' 
          ? 'Could not extract document data. Make sure the photo shows the BACK of the document with the MRZ lines clearly visible.' 
          : 'Impossibile estrarre i dati. Assicurati che la foto mostri il RETRO del documento con le righe MRZ ben visibili.');
        setScanningMRZ(false);
        return;
      }
      
      // Show what was found
      const foundFields = [];
      if (mrzData.nome) foundFields.push(mrzData.nome);
      if (mrzData.cognome) foundFields.push(mrzData.cognome);
      if (mrzData.numero_documento) foundFields.push(`Doc: ${mrzData.numero_documento}`);
      if (mrzData.data_nascita) foundFields.push(`Nato: ${mrzData.data_nascita}`);
      
      // Auto-fill form with parsed data
      setOspitePrincipale(prev => ({
        ...prev,
        nome: mrzData.nome || prev.nome,
        cognome: mrzData.cognome || prev.cognome,
        data_nascita: mrzData.data_nascita || prev.data_nascita,
        nazionalita: mrzData.nazionalita || prev.nazionalita,
        tipo_documento: mrzData.tipo_documento || prev.tipo_documento,
        numero_documento: mrzData.numero_documento || prev.numero_documento,
        scadenza_documento: mrzData.scadenza_documento || prev.scadenza_documento
      }));
      
      if (foundFields.length > 0) {
        toast.success(language === 'en' 
          ? `Data extracted: ${foundFields.join(', ')}` 
          : `Dati estratti: ${foundFields.join(', ')}`);
      } else {
        toast.warning(language === 'en'
          ? 'Some data extracted but may be incomplete. Please verify the form.'
          : 'Alcuni dati estratti ma potrebbero essere incompleti. Verifica il modulo.');
      }
      
    } catch (error) {
      console.error('OCR error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error(language === 'en' 
          ? 'Analysis took too long. Try with a smaller/clearer image.' 
          : 'Analisi troppo lunga. Prova con un\'immagine più piccola/nitida.');
      } else {
        toast.error(language === 'en' 
          ? 'Error analyzing document. Please try again.' 
          : 'Errore analisi documento. Riprova.');
      }
    } finally {
      setScanningMRZ(false);
    }
  };

  const addAccompagnatore = () => {
    if (accompagnatori.length < parseInt(numOspiti) - 1) {
      setAccompagnatori([...accompagnatori, {
        nome: '',
        cognome: '',
        data_nascita: '',
        luogo_nascita: '',
        nazionalita: 'Italiana',
        tipo_documento: 'carta_identita',
        numero_documento: ''
      }]);
    }
  };

  const updateAccompagnatore = (index, field, value) => {
    const updated = [...accompagnatori];
    updated[index][field] = value;
    setAccompagnatori(updated);
  };

  const removeAccompagnatore = (index) => {
    setAccompagnatori(accompagnatori.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verifica email o codice prenotazione
    if (!emailPrenotazione && !codicePrenotazione) {
      toast.error('Inserisci l\'email o il codice della prenotazione');
      return;
    }
    
    if (!arrivo || !partenza) {
      toast.error('Seleziona le date di arrivo e partenza');
      return;
    }

    if (partenza <= arrivo) {
      toast.error('La data di partenza deve essere successiva all\'arrivo');
      return;
    }

    if (!ospitePrincipale.nome || !ospitePrincipale.cognome || !ospitePrincipale.numero_documento) {
      toast.error('Compila tutti i dati dell\'ospite principale');
      return;
    }

    if (!ospitePrincipale.foto_fronte_url || !ospitePrincipale.foto_retro_url) {
      toast.error('Carica le foto fronte e retro del documento');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/checkin`, {
        email_prenotazione: emailPrenotazione || null,
        codice_prenotazione: codicePrenotazione || null,
        data_arrivo: format(arrivo, 'yyyy-MM-dd'),
        data_partenza: format(partenza, 'yyyy-MM-dd'),
        num_ospiti: parseInt(numOspiti),
        note: note || null,
        ospite_principale: ospitePrincipale,
        accompagnatori: accompagnatori
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success('Check-in effettuato con successo!');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il check-in');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (success) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-cinzel text-3xl text-[#1A202C] mb-4">
            {t.checkinSuccess}
          </h1>
          <p className="font-manrope text-[#4A5568] mb-2">
            {t.checkinSuccessDesc}
          </p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-[#C5A059] hover:bg-[#B8956F]"
          >
            {t.backToDashboard}
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-2">
            {language === 'en' ? 'Stay' : 'Soggiorno'}
          </p>
          <h1 className="font-cinzel text-4xl text-[#1A202C] mb-4">
            {t.onlineCheckin}
          </h1>
          <p className="font-manrope text-[#4A5568]">
            {language === 'en' ? 'Fill in stay details and upload guest documents' : 'Compila i dati del soggiorno e carica i documenti degli ospiti'}
          </p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: t.step1 },
            { num: 2, label: t.step2 },
            { num: 3, label: t.step3 }
          ].map((step) => (
            <div key={step.num} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-cinzel ${
                  currentStep >= step.num 
                    ? 'bg-[#C5A059] text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step.num}
              </div>
              {step.num < 3 && (
                <div className={`w-12 h-1 mx-2 ${currentStep > step.num ? 'bg-[#C5A059]' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Pending Booking Banner - Auto-detected booking */}
          {pendingBooking && currentStep === 1 && (
            <div className="mb-6 bg-gradient-to-r from-[#1B4332] to-[#2D5A45] rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#C5A059] flex items-center justify-center flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-cinzel text-lg mb-1">
                    {language === 'en' ? 'Booking Found!' : 'Prenotazione Trovata!'}
                  </h3>
                  <p className="text-sm text-gray-200 mb-3">
                    {language === 'en' 
                      ? 'Your data has been pre-filled. Just add your document details.' 
                      : 'I tuoi dati sono stati precompilati. Aggiungi solo i dati del documento.'}
                  </p>
                  
                  {/* Booking details summary */}
                  <div className="bg-white/10 rounded-lg p-3 mb-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-300">{language === 'en' ? 'Code:' : 'Codice:'}</span>
                        <span className="ml-2 font-mono font-bold text-[#C5A059]">{pendingBooking.codice_prenotazione}</span>
                      </div>
                      <div>
                        <span className="text-gray-300">{language === 'en' ? 'Guests:' : 'Ospiti:'}</span>
                        <span className="ml-2 font-semibold">{pendingBooking.num_ospiti}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-300">{language === 'en' ? 'Dates:' : 'Date:'}</span>
                        <span className="ml-2 font-semibold">
                          {new Date(pendingBooking.data_arrivo).toLocaleDateString('it-IT')} → {new Date(pendingBooking.data_partenza).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                      {pendingBooking.unit_nome && (
                        <div className="col-span-2">
                          <span className="text-gray-300">{language === 'en' ? 'Unit:' : 'Casetta:'}</span>
                          <span className="ml-2 font-semibold text-[#C5A059]">{pendingBooking.unit_nome}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="bg-[#C5A059] hover:bg-[#B08D45] text-white"
                  >
                    {language === 'en' ? 'Continue to Document Data' : 'Continua ai Dati Documento'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Date e numero ospiti */}
          {currentStep === 1 && (
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-cinzel text-xl">{t.bookingVerification}</CardTitle>
                <CardDescription className="font-manrope">
                  {t.enterBookingDetails}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sezione verifica prenotazione */}
                <div className="bg-[#F9F9F7] p-4 rounded-lg border border-[#C5A059]/20">
                  <p className="font-manrope text-sm text-[#4A5568] mb-4">
                    {language === 'en' ? 'Enter your booking email or code to proceed' : "Inserisci l'email o il codice della tua prenotazione per procedere con il check-in"}
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#C5A059]" />
                        {t.bookingEmail}
                      </Label>
                      <Input
                        type="email"
                        placeholder="esempio@email.com"
                        value={emailPrenotazione}
                        onChange={(e) => setEmailPrenotazione(e.target.value)}
                        className="border-[#E2E8F0]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-[#C5A059]" />
                        {t.bookingCode}
                      </Label>
                      <Input
                        placeholder="MDP-XXXXXX"
                        value={codicePrenotazione}
                        onChange={(e) => setCodicePrenotazione(e.target.value.toUpperCase())}
                        className="border-[#E2E8F0] uppercase"
                      />
                    </div>
                  </div>
                  <p className="font-manrope text-xs text-[#718096] mt-2">
                    * {language === 'en' ? 'Only one field is required' : 'È sufficiente inserire uno dei due campi'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t.arrivalDate}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-[#E2E8F0] ${!arrivo && 'text-muted-foreground'}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#C5A059]" />
                          {arrivo ? format(arrivo, 'dd MMMM yyyy', { locale: dateLocale }) : t.selectDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={arrivo}
                          onSelect={setArrivo}
                          locale={dateLocale}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>{t.departureDate}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-[#E2E8F0] ${!partenza && 'text-muted-foreground'}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#C5A059]" />
                          {partenza ? format(partenza, 'dd MMMM yyyy', { locale: dateLocale }) : t.selectDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={partenza}
                          onSelect={setPartenza}
                          locale={dateLocale}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date <= (arrivo || today);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t.numberOfGuests}</Label>
                  <Select value={numOspiti} onValueChange={(v) => {
                    setNumOspiti(v);
                    // Rimuovi accompagnatori extra
                    if (accompagnatori.length >= parseInt(v)) {
                      setAccompagnatori(accompagnatori.slice(0, parseInt(v) - 1));
                    }
                  }}>
                    <SelectTrigger className="border-[#E2E8F0]">
                      <Users className="w-4 h-4 mr-2 text-[#C5A059]" />
                      <SelectValue placeholder={language === 'en' ? 'Select' : 'Seleziona'} />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n} {n === 1 ? t.guest : t.guests}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t.notes} ({language === 'en' ? 'optional' : 'opzionale'})</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t.specialRequests}
                    className="border-[#E2E8F0]"
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  disabled={!arrivo || !partenza}
                  className="w-full bg-[#C5A059] hover:bg-[#B08D45]"
                >
                  {t.continue}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Ospite Principale */}
          {currentStep === 2 && (
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-cinzel text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-[#C5A059]" />
                  {t.mainGuest}
                </CardTitle>
                <CardDescription className="font-manrope">
                  {language === 'en' ? 'Enter data and upload document photos' : 'Inserisci i dati e carica le foto del documento'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t.firstName} *</Label>
                    <Input
                      value={ospitePrincipale.nome}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t.lastName} *</Label>
                    <Input
                      value={ospitePrincipale.cognome}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, cognome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t.birthDate} *</Label>
                    <Input
                      type="date"
                      value={ospitePrincipale.data_nascita}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, data_nascita: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t.birthPlace} *</Label>
                    <Input
                      value={ospitePrincipale.luogo_nascita}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, luogo_nascita: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t.nationality} *</Label>
                    <Select 
                      value={ospitePrincipale.nazionalita} 
                      onValueChange={(v) => setOspitePrincipale({...ospitePrincipale, nazionalita: v})}
                    >
                      <SelectTrigger className="border-[#E2E8F0]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {t.nationalities.map(n => (
                          <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t.documentType} *</Label>
                    <Select 
                      value={ospitePrincipale.tipo_documento} 
                      onValueChange={(v) => setOspitePrincipale({...ospitePrincipale, tipo_documento: v})}
                    >
                      <SelectTrigger className="border-[#E2E8F0]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {t.documentTypes.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t.documentNumber} *</Label>
                    <Input
                      value={ospitePrincipale.numero_documento}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, numero_documento: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t.documentExpiry} *</Label>
                    <Input
                      type="date"
                      value={ospitePrincipale.scadenza_documento}
                      onChange={(e) => setOspitePrincipale({...ospitePrincipale, scadenza_documento: e.target.value})}
                      required
                    />
                  </div>
                </div>

                {/* Document Photos - Camera or Upload */}
                <div className="border-t pt-6 mt-6">
                  <Label className="text-base font-medium mb-2 block">
                    {language === 'en' ? 'Document Photo' : 'Foto Documento'}
                  </Label>
                  <p className="text-sm text-gray-500 mb-4">
                    {isNative 
                      ? (language === 'en' ? 'Take a photo or select from gallery' : 'Scatta una foto o seleziona dalla galleria')
                      : (language === 'en' ? 'Upload document photos from your device' : 'Carica le foto del documento dal dispositivo')
                    }
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Front document */}
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      ospitePrincipale.foto_fronte_url ? 'border-green-500 bg-green-50' : 'border-gray-300'
                    }`}>
                      {uploadingFronte ? (
                        <div className="py-2">
                          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full mx-auto mb-2" />
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Uploading...' : 'Caricamento...'}</p>
                        </div>
                      ) : ospitePrincipale.foto_fronte_url ? (
                        <>
                          <Check className="w-8 h-8 text-green-600 mx-auto mb-1" />
                          <p className="text-sm text-green-700">{language === 'en' ? 'Front OK' : 'Fronte OK'}</p>
                          <button 
                            type="button"
                            onClick={() => isNative ? handleCameraCapture('fronte') : null}
                            className="text-xs text-[#C5A059] underline mt-1"
                          >
                            {language === 'en' ? 'Retake' : 'Rifai'}
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-700 mb-2">{language === 'en' ? 'Front' : 'Fronte'}</p>
                          {isNative ? (
                            <button
                              type="button"
                              onClick={() => handleCameraCapture('fronte')}
                              className="inline-flex items-center gap-1.5 bg-[#C5A059] text-white px-3 py-2 rounded text-sm font-medium"
                            >
                              <Camera className="w-4 h-4" />
                              {language === 'en' ? 'Capture' : 'Cattura'}
                            </button>
                          ) : (
                            <label className="inline-flex items-center gap-1.5 bg-[#C5A059] text-white px-3 py-2 rounded text-sm cursor-pointer">
                              <Upload className="w-4 h-4" />
                              {language === 'en' ? 'Upload' : 'Carica'}
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.heic,.heif,.webp"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'fronte')}
                              />
                            </label>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Back document */}
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                      ospitePrincipale.foto_retro_url ? 'border-green-500 bg-green-50' : 'border-gray-300'
                    }`}>
                      {uploadingRetro ? (
                        <div className="py-2">
                          <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full mx-auto mb-2" />
                          <p className="text-sm text-gray-500">{language === 'en' ? 'Uploading...' : 'Caricamento...'}</p>
                        </div>
                      ) : ospitePrincipale.foto_retro_url ? (
                        <>
                          <Check className="w-8 h-8 text-green-600 mx-auto mb-1" />
                          <p className="text-sm text-green-700">{language === 'en' ? 'Back OK' : 'Retro OK'}</p>
                          <button 
                            type="button"
                            onClick={() => isNative ? handleCameraCapture('retro') : null}
                            className="text-xs text-[#C5A059] underline mt-1"
                          >
                            {language === 'en' ? 'Retake' : 'Rifai'}
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-700 mb-2">{language === 'en' ? 'Back' : 'Retro'}</p>
                          {isNative ? (
                            <button
                              type="button"
                              onClick={() => handleCameraCapture('retro')}
                              className="inline-flex items-center gap-1.5 bg-[#C5A059] text-white px-3 py-2 rounded text-sm font-medium"
                            >
                              <Camera className="w-4 h-4" />
                              {language === 'en' ? 'Capture' : 'Cattura'}
                            </button>
                          ) : (
                            <label className="inline-flex items-center gap-1.5 bg-[#C5A059] text-white px-3 py-2 rounded text-sm cursor-pointer">
                              <Upload className="w-4 h-4" />
                              {language === 'en' ? 'Upload' : 'Carica'}
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.heic,.heif,.webp"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'retro')}
                              />
                            </label>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="flex-1"
                  >
                    {t.back}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    disabled={!ospitePrincipale.nome || !ospitePrincipale.cognome || !ospitePrincipale.numero_documento}
                    className="flex-1 bg-[#C5A059] hover:bg-[#B08D45]"
                  >
                    {parseInt(numOspiti) > 1 
                      ? (language === 'en' ? 'Continue with Companions' : 'Continua con Accompagnatori') 
                      : (language === 'en' ? 'Summary' : 'Riepilogo')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Accompagnatori e Riepilogo */}
          {currentStep === 3 && (
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-cinzel text-xl">
                  {parseInt(numOspiti) > 1 
                    ? (language === 'en' ? 'Companions and Summary' : 'Accompagnatori e Riepilogo') 
                    : t.summary}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Accompagnatori section */}
                {parseInt(numOspiti) > 1 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">
                        {t.companions} ({accompagnatori.length}/{parseInt(numOspiti) - 1})
                      </Label>
                      {accompagnatori.length < parseInt(numOspiti) - 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addAccompagnatore}
                        >
                          <Plus className="w-4 h-4 mr-1" /> {language === 'en' ? 'Add' : 'Aggiungi'}
                        </Button>
                      )}
                    </div>

                    {accompagnatori.map((acc, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-4">
                          <span className="font-medium">{t.companion} {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAccompagnatore(index)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>Nome *</Label>
                            <Input
                              value={acc.nome}
                              onChange={(e) => updateAccompagnatore(index, 'nome', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label>Cognome *</Label>
                            <Input
                              value={acc.cognome}
                              onChange={(e) => updateAccompagnatore(index, 'cognome', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label>Data di Nascita *</Label>
                            <Input
                              type="date"
                              value={acc.data_nascita}
                              onChange={(e) => updateAccompagnatore(index, 'data_nascita', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label>Luogo di Nascita *</Label>
                            <Input
                              value={acc.luogo_nascita}
                              onChange={(e) => updateAccompagnatore(index, 'luogo_nascita', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label>Nazionalità *</Label>
                            <Select 
                              value={acc.nazionalita} 
                              onValueChange={(v) => updateAccompagnatore(index, 'nazionalita', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {t.nationalities.map(n => (
                                  <SelectItem key={n} value={n}>{n}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Tipo Documento</Label>
                            <Select 
                              value={acc.tipo_documento} 
                              onValueChange={(v) => updateAccompagnatore(index, 'tipo_documento', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {t.documentTypes.map(d => (
                                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2">
                            <Label>{t.documentNumber}</Label>
                            <Input
                              value={acc.numero_documento}
                              onChange={(e) => updateAccompagnatore(index, 'numero_documento', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Riepilogo */}
                <div className="bg-[#F9F9F7] p-6 rounded-lg border border-[#E2E8F0]">
                  <h3 className="font-cinzel text-lg text-[#1A202C] mb-4">{t.summary}</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#4A5568]">{language === 'en' ? 'Dates' : 'Date'}</span>
                      <span className="font-medium">
                        {format(arrivo, 'dd MMM', { locale: dateLocale })} - {format(partenza, 'dd MMM yyyy', { locale: dateLocale })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4A5568]">{language === 'en' ? 'Nights' : 'Notti'}</span>
                      <span className="font-medium">
                        {Math.ceil((partenza - arrivo) / (1000 * 60 * 60 * 24))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#4A5568]">{language === 'en' ? 'Guests' : 'Ospiti'}</span>
                      <span className="font-medium">{numOspiti}</span>
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <span className="text-[#4A5568]">{t.mainGuest}</span>
                      <p className="font-medium">{ospitePrincipale.nome} {ospitePrincipale.cognome}</p>
                      <p className="text-xs text-[#4A5568]">
                        Doc: {ospitePrincipale.numero_documento} • {ospitePrincipale.nazionalita}
                      </p>
                    </div>
                    {accompagnatori.length > 0 && (
                      <div className="border-t pt-3">
                        <span className="text-[#4A5568]">{t.companions}</span>
                        {accompagnatori.map((acc, i) => (
                          <p key={i} className="font-medium">{acc.nome} {acc.cognome}</p>
                        ))}
                      </div>
                    )}
                    {note && (
                      <div className="border-t pt-3">
                        <span className="text-[#4A5568]">{t.notes}</span>
                        <p className="font-medium">{note}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                    className="flex-1"
                  >
                    {t.back}
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-[#C5A059] hover:bg-[#B08D45]"
                  >
                    {loading ? (language === 'en' ? 'Processing...' : 'Elaborazione...') : t.confirm}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </div>
    </Layout>
  );
}
